import {DateTime} from 'luxon';
import {decodeDelimitedArray} from 'use-query-params';

import {ForecastTime} from './api/series';

export const TimeZoneUTC = 'UTC';
export const TimeZoneUTCDisplayName = 'Coordinated Universal Time (UTC)';

export function isValidDate(date: any): boolean {
  return date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date);
}

export function makeDateArrayQueryParam(defaultDates: Date[]) {
  return {
    encode: (dates: Date[]) => {
      return dates
        .filter(isValidDate)
        .map((d) => d.toISOString())
        .join('|');
    },
    decode: (datesStr: string | (string | null)[] | null | undefined): Date[] => {
      if (datesStr) {
        const isoStrings = decodeDelimitedArray(datesStr, '|') as [string, string];
        try {
          return isoStrings.map((iso) => new Date(iso)).filter(isValidDate);
        } catch {
          return defaultDates;
        }
      }
      return defaultDates;
    },
  };
}

export function makeDateRangeQueryParam(defaultDateRange: [Date, Date]) {
  return {
    encode: (dateRange: [Date, Date]) => {
      return `${dateRange[0].toISOString()}|${dateRange[1].toISOString()}`;
    },
    decode: (dateRangeStr: string | (string | null)[] | null | undefined): [Date, Date] => {
      if (dateRangeStr) {
        const isoStrings = decodeDelimitedArray(dateRangeStr, '|') as [string, string];
        try {
          return [new Date(isoStrings[0]), new Date(isoStrings[1])];
        } catch {
          return defaultDateRange;
        }
      }
      return defaultDateRange;
    },
  };
}

export function formatDateDay(date: Date, locale: string, timeZone?: string): string {
  const dateTime = DateTime.fromJSDate(date, {zone: timeZone});
  return dateTime.setLocale(locale).toLocaleString({month: 'long', day: 'numeric'});
}

export function formatDateMonth(date: Date, timeZone?: string): string {
  const dateTime = DateTime.fromJSDate(date, {zone: timeZone});
  return dateTime.toFormat('LLL yyyy');
}

export function formatDateShortFull(date: Date, timeZone: string): string {
  const dateTime = DateTime.fromJSDate(date, {zone: timeZone});
  // yyyy-MM-dd hh:mm in local timezone
  return `${dateTime.year}-${_addLeadingZero(dateTime.month)}-${_addLeadingZero(
    dateTime.day
  )} ${_addLeadingZero(dateTime.hour)}:${_addLeadingZero(dateTime.minute)}`;
}

export function formatDateMonthDayTime(date: Date, locale: string, timeZone: string): string {
  // Oct 22, 06:00 in local timezone
  return DateTime.fromJSDate(date, {zone: timeZone}).setLocale(locale).toFormat('LLL d, HH:mm');
}

export function formatDateShort(date: Date, timeZone?: string): string {
  const dateTime = DateTime.fromJSDate(date, {zone: timeZone});
  // yyyy-MM-dd in local timezone
  return `${dateTime.year}-${_addLeadingZero(dateTime.month)}-${_addLeadingZero(dateTime.day)}`;
}

export function now(zone: string) {
  return DateTime.now().setZone(zone);
}

function _addLeadingZero(number: number) {
  return String(number).padStart(2, '0');
}

export function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function addUTCDays(date: Date, days: number) {
  // Add days to the date in UTC. This ensures that we always add
  // exact 24-hour periods, even across time changes (like daylight savings).
  const utcDateTime = DateTime.fromJSDate(date).toUTC();
  const adjustedUTC = utcDateTime.plus({days});
  return adjustedUTC.toJSDate();
}

export function addSeconds(date: Date, seconds: number) {
  const d = new Date(date);
  d.setSeconds(d.getSeconds() + seconds);
  return d;
}

export function addMilliseconds(date: Date, milliseconds: number) {
  const d = new Date(date);
  d.setMilliseconds(d.getMilliseconds() + milliseconds);
  return d;
}

export function addMonths(date: Date, months: number, timeZone: string) {
  return DateTime.fromJSDate(date).setZone(timeZone).plus({months}).toJSDate();
}

export function startOfMonth(date: Date, timeZone: string) {
  return DateTime.fromJSDate(date).setZone(timeZone).startOf('month').toJSDate();
}

export function endOfMonth(date: Date, timeZone: string) {
  return DateTime.fromJSDate(date).setZone(timeZone).endOf('month').toJSDate();
}

export function areValidDateParts(year?: number, month?: number, day?: number) {
  if (!(year || month || day)) {
    return true;
  }
  const dateObject = {
    ...(year && {year}),
    ...(month && {month}),
    ...(day && {day}),
  };
  const dateTime = DateTime.fromObject(dateObject);
  return dateTime.isValid;
}

const _MS_PER_DAY = 1000 * 60 * 60 * 24;
export function diffDays(a: Date, b: Date): number {
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor(Math.abs(utc2 - utc1) / _MS_PER_DAY);
}

export function maxDate(ds: Date[]) {
  return ds.reduce((maxD, d) => (d > maxD ? d : maxD), ds[0]);
}

export function minDate(ds: Date[]) {
  return ds.reduce((minD, d) => (d < minD ? d : minD), ds[0]);
}

const OCTOBER = 9;
export function getWaterYearDateRange(date: Date): [Date, Date] {
  // if after october, use current year, otherwise use last year
  let startYear, endYear;
  if (date.getMonth() >= OCTOBER) {
    startYear = date.getFullYear();
    endYear = startYear + 1;
  } else {
    endYear = date.getFullYear();
    startYear = endYear - 1;
  }
  return [new Date(startYear, OCTOBER, 1), new Date(endYear, OCTOBER, 1)];
}

export function getYearDateRange(date: Date): [Date, Date] {
  const year = date.getFullYear();
  return [new Date(year, 0, 1), new Date(year, 11, 31)];
}

export function getNow() {
  return new Date();
}

export function makeGetSelectionForIntervalShortcuts(
  selectedForecastTimes: ForecastTime[],
  offsetDays: number[],
  maxDate: Date
) {
  return function (forecastTimes: ForecastTime[]) {
    const latest = selectedForecastTimes.length
      ? getLatestForecastTime(selectedForecastTimes)
      : findClosestForecastTime(forecastTimes, maxDate.getTime());
    if (!latest) return [];

    const initTimes = offsetDays.map((d) => addDays(latest.initializationTime, -1 * d).getTime());

    return [latest, ...initTimes.flatMap((t) => findClosestForecastTime(forecastTimes, t) ?? [])];
  };
}

type Initialized = Pick<ForecastTime, 'initializationTime'>;

export function findClosestForecastTime<T extends Initialized>(
  forecastTimes: T[],
  time: number
): T | undefined {
  const forecastTimeDists = forecastTimes.map((t) =>
    Math.abs(time - t.initializationTime.getTime())
  );

  const minForecastTimeDistIdx = forecastTimeDists.indexOf(Math.min(...forecastTimeDists));

  return forecastTimes[minForecastTimeDistIdx];
}

export function getLatestForecastTime<T extends Initialized>(forecastTimes: T[]): T | undefined {
  const forecastTimesForKey = forecastTimes.map((t) => t.initializationTime.getTime());

  const maxForecastTimeIdx = forecastTimesForKey.indexOf(Math.max(...forecastTimesForKey));

  return forecastTimes[maxForecastTimeIdx];
}

// The candidate nearest a reference run, if it is within a day of it.
export function closestForecastTimeWithinDay<T extends Initialized>(
  reference: Initialized,
  candidates: T[]
): T | undefined {
  const closest = findClosestForecastTime(candidates, reference.initializationTime.getTime());
  if (!closest) return undefined;
  const distance = Math.abs(
    closest.initializationTime.getTime() - reference.initializationTime.getTime()
  );
  return distance < _MS_PER_DAY ? closest : undefined;
}
